import { useState, memo, useEffect } from "react";
import { FaImage, FaTimes, FaPaperPlane, FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ROUTERS } from "utils/router";
import "./style.scss";
import { createCapsule } from "api/Capsule.api"; // Import your API function
import { getListFriends } from "api/User.api";

const CreateCapsulePage = ({ onPost }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [privacy, setPrivacy] = useState("Public");
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [friendsList, setFriendsList] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [memoryQuestions, setMemoryQuestions] = useState([
    {
      question: "",
      answerA: "",
      answerB: "",
      answerC: "",
      answerD: "",
      correctAnswer: "",
      explanation: ""
    }
  ]);

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const friends = await getListFriends();
        setFriendsList(friends.data || []);
        console.log("Fetched friends list:", friends.data);
      } catch (error) {
        console.error("Failed to fetch friends list:", error);
      }
    };
    fetchFriends();
  }, []);

  const handlePost = async () => {
    try {
      if (!content.trim() && images.length === 0) {
        throw new Error("Content or images must be provided.");
      }

      // Prepare the data to match the NewCapsule typedef
      const capsuleData = {
        content,
        contributors: selectedFriends,
        viewers: privacy === "Private" ? selectedFriends : [],
        privacy,
        recallQuestions: memoryQuestions.map((q) => ({
          capsuleId: "", // This can be omitted or set later
          question: q.question,
          choicesA: q.answerA, // Replace with actual choices if available
          choicesB: q.answerB,
          choicesC: q.answerC,
          choicesD: q.answerD,
          correctAnswer: q.correctAnswer || "", // Replace with the correct answer
          explanation: q.explanation || "", // Replace with an explanation if available
        })),
        notificationInterval: 5, // Default to 7 days
        openingTime: new Date(scheduleDate),
        files: images?.map((img) => ({
          buffer: img.buffer, // Original file object
          originalname: img.originalname, // File name
          mimetype: img.mimetype, // File MIME type
        })),
      };

      try {
        const response = await createCapsule(capsuleData); // Call the API
        console.log("Capsule created successfully:", response);

        // Reset the form after successful submission
        setContent("");
        setImages([]);
        setAttachments([]);
        setPrivacy("public");
        setSelectedFriends([]);
        setScheduleDate("");
        setMemoryQuestions([{ question: "", answer: "" }]);
        setStep(1);

        // Navigate to MyCapsule page after creation
        navigate(ROUTERS.USER.MYCAPSULE);
      } catch (apiError) {
        console.log("Capsule content:", capsuleData);
        console.error("Error creating capsule:", apiError);
        alert("Failed to create capsule. Please try again.");
      }
    } catch (validationError) {
      console.error("Validation error:", validationError);
      alert(validationError.message);
    }
  };


  // Helper function to convert ArrayBuffer to Base64 string
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = [];
    let filesProcessed = 0;

    files.forEach((file) => {
      const reader = new FileReader();

      reader.onloadend = () => {

        // Convert ArrayBuffer to Base64 string for buffer
        const base64Buffer = arrayBufferToBase64(reader.result);

        newImages.push({
          buffer: base64Buffer, // Base64 encoded string
          originalname: file.name, // File name
          mimetype: file.type, // File MIME type
          preview: URL.createObjectURL(file), // Temporary URL for preview
        });

        filesProcessed++;
        if (filesProcessed === files.length) {
          setImages((prevImages) => [...prevImages, ...newImages]);
        }
      };

      reader.readAsArrayBuffer(file); // Read binary content
    });
  };

  const handleRemoveImage = (index) => {
    // Revoke the object URL to release memory
    URL.revokeObjectURL(images[index].preview);
    setImages(images.filter((_, i) => i !== index));
  };


  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };


  const handleFriendSelection = (friend) => {
    setSelectedFriends((prev) => {
      const friendId = friend.id; // support both userId or id
      if (prev.includes(friendId)) {
        return prev.filter((f) => f !== friendId);
      } else {
        return [...prev, friendId];
      }
    });
  };

  const handleMemoryChange = (index, field, value) => {
    const updated = [...memoryQuestions];
    updated[index][field] = value;
    setMemoryQuestions(updated);
  };

  const addMemoryQuestion = () => {
    setMemoryQuestions([...memoryQuestions, { question: "", answer: "" }]);
  };


  return (
    <div className="post-form">
      {step === 1 && (
        <>
          <div className="actions">
            <select className="privacy-select" value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
              <option value="Public">🌍 Public</option>
              <option value="Private">👥 Friends</option>
            </select>
            <label>
              <button onClick={() => document.getElementById("imageInput").click()}>
                <FaImage /> Picture/Video
                <input
                  id="imageInput"
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleImageUpload}
                  multiple
                  hidden
                />
              </button>
            </label>
          </div>

          {privacy === "Private" && (
            <div className="friend-selector">
              <p>Select friends:</p>
              <input
                type="text"
                placeholder="Search friends..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
          <div className="friend-list">
            {friendsList.length > 0 ? (
              friendsList.map((friend) => (
                <div
                  className={`friend-avatar ${selectedFriends.includes(friend.id) ? 'selected' : ''}`}
                  key={friend.id}
                  onClick={() => handleFriendSelection(friend)}
                >
                  <img src={friend.avatarUrl || "/default-avatar.jpeg"} alt={friend.displayName || friend.email} />
                  <span>{friend.displayName || friend.email}</span>
                </div>

              ))
            ) : (
              <p className="no-result">No result</p>
            )}
          </div>
            </div>
          )}
          <textarea
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />

          {images.length > 0 && (
            <div className="images-preview">
              {images.map((img, index) => (
                <div key={index} className="image-item">
                  <img src={img} alt={`preview-${index}`} className="preview-img" />
                  <button className="remove-btn" onClick={() => handleRemoveImage(index)}>
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          )}

          {attachments.length > 0 && (
            <div className="attachments-list">
              {attachments.map((file, index) => (
                <div key={index} className="attachment-item">
                  📄 {file.name}
                  <button className="remove-att-btn" onClick={() => removeAttachment(index)}>
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setStep(2)}><FaArrowRight /> Next</button>
        </>
      )}

      {step === 2 && (
        <div className="schedule-step">
          <label>
            <p>Schedule:</p>
            <input
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => {
                const selectedDate = e.target.value; // This is already in the correct format
                console.log("Selected date:", selectedDate);
                setScheduleDate(selectedDate);
              }}
            />
          </label>

          <div className="memory-questions">
            <p>To remind you:</p>
            {memoryQuestions.map((q, index) => (
              <div key={index} className="memory-question">
                <input type="text" placeholder="Question" value={q.question} onChange={(e) => handleMemoryChange(index, "question", e.target.value)} />
                <div className="answer-row">
                  <input type="text" className="answer" placeholder="Answer A" value={q.answerA} onChange={(e) => handleMemoryChange(index, "answerA", e.target.value)} />
                  <input type="text" className="answer" placeholder="Answer B" value={q.answerB} onChange={(e) => handleMemoryChange(index, "answerB", e.target.value)} />
                  <input type="text" className="answer" placeholder="Answer C" value={q.answerC} onChange={(e) => handleMemoryChange(index, "answerC", e.target.value)} />
                  <input type="text" className="answer" placeholder="Answer D" value={q.answerD} onChange={(e) => handleMemoryChange(index, "answerD", e.target.value)} />
                </div>
                <div className="correct-answer-selection">
                  <label>
                    <p>Correct Answer:</p>
                    <select
                      value={q.correctAnswer}
                      onChange={(e) => handleMemoryChange(index, "correctAnswer", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </label>
                </div>
                <textarea
                  placeholder="Explain why this is the correct answer"
                  value={q.explanation}
                  onChange={(e) => handleMemoryChange(index, "explanation", e.target.value)}
                  className="explanation"
                />
              </div>
            ))}
            <button type="button" onClick={addMemoryQuestion}>+ Add question</button>
          </div>
          <div className="step-navigation">
            <button onClick={() => setStep(1)}><FaArrowLeft /> Back</button>
            <button onClick={handlePost}><FaPaperPlane /> Post</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(CreateCapsulePage);
